import { ChangeEvent, useState, useEffect } from "react";
import styles from "./NotifyMeForm.module.scss";
import { TextInput } from "@JohnLewisPartnership/jl-design-system/dist/components/text-input/TextInput";
import { getUserEmail } from "../util/getUserEmail";
import { getNotificationsApiUrl, notifyMe } from "../util/notifyMe";
import { NotifiableEvent } from "@/types/types";
import { dispatchAnalyticsEvent } from "@/utils";
import { SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED } from "@/constants/constants";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { PrimaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";

type Props = {
  pimProductId: string;
  skuId: string;
  onSuccess: () => void;
  notifiableEventType?: NotifiableEvent;
  notificationsApiBaseUrl: string;
  notificationsApiKey: string;
  isSelected: boolean;
};

export const NotifyMeForm = ({
  pimProductId,
  skuId,
  isSelected,
  onSuccess,
  notificationsApiBaseUrl,
  notificationsApiKey,
  notifiableEventType,
}: Props) => {
  const [formErrorMessage, setFormErrorMessage] = useState({
    title: "",
    body: "",
  });
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailRegex =
    /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;

  const emailErrors = {
    required: "Enter an email address",
    format: "Enter an email address in the correct format: name@example.com",
  };

  const formErrors = {
    apiError: {
      title: "We're sorry, but something's not working right now",
      body: "Please try again later",
    },
  };

  useEffect(() => {
    setEmail(getUserEmail());
  }, []);

  if (!notifiableEventType) {
    return null;
  }

  const title = EmailMeMessaging[notifiableEventType];

  const notificationsApiUrl = getNotificationsApiUrl(
    notifiableEventType,
    notificationsApiBaseUrl,
    notificationsApiKey,
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (formErrorMessage.title) {
      setFormErrorMessage({ title: "", body: "" });
    }
    if (emailErrorMessage) {
      setEmailErrorMessage("");
    }
  };

  const handleSubmit = async () => {
    if (!email) {
      setEmailErrorMessage(emailErrors.required);
      return;
    }

    if (email && !email.match(emailRegex)) {
      setEmailErrorMessage(emailErrors.format);
      return;
    }

    if (!isSelected) {
      window.dispatchEvent(
        new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
      );
      return;
    }

    setIsSubmitting(true);

    const { confirmationEmailSent, showNotifyError } = await notifyMe({
      email,
      pimProductId,
      skuId,
      notificationsApiUrl,
      notifiableEventType,
    });

    setIsSubmitting(false);

    if (confirmationEmailSent) onSuccess();
    if (showNotifyError)
      setFormErrorMessage({
        title: formErrors.apiError.title,
        body: formErrors.apiError.body,
      });
    dispatchMonetateQEvent("pdp:notifyme:button:click");
    dispatchAnalyticsEvent("global.emwbisSubmitted", {}, {});
  };

  return (
    <>
      <h2 data-testid="notifyme:header" className={styles.header}>
        {title}
      </h2>
      <form
        key={skuId}
        data-testid="notifyme-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {formErrorMessage.title && (
          <DEPRECATED_Message
            type="error"
            title={formErrorMessage.title}
            body={formErrorMessage.body}
            className={styles.errorMessage}
          />
        )}
        <TextInput
          id="emailAddress"
          name="email"
          type="text"
          data-testid="notifyme-form-email"
          value={email}
          onChange={handleChange}
          autoComplete="email"
          labelText="Please enter email"
          errorText={emailErrorMessage}
          className={styles.email}
          maxLength={40}
        />
        <PrimaryButton
          tag="button"
          type="button"
          submitting={isSubmitting}
          onClick={handleSubmit}
          data-testid="notifyme-form-submit-button"
          labelText="Notify me"
          width="full-width"
          className={styles.notifyMeButton}
        />
      </form>
    </>
  );
};

export const EmailMeMessaging: Record<NotifiableEvent, string> = {
  backInStock: "Email me when available",
  onRelease: "Email me when available",
  onPreorderLaunch: "Email me when available to pre-order",
  newlyInStock: "Email me when available",
};
